/**
 * i18n — Système de traduction multi-tenant.
 * Porté depuis Sauf Imprévu, rendu framework-agnostic.
 *
 * Stockage de la langue persisté via un callback injectable
 * (localStorage en web, AsyncStorage/Preferences en natif).
 */

export type Lang = 'fr' | 'en' | 'es' | 'it' | 'de' | 'pt';

export const LANGS: Lang[] = ['fr', 'en', 'es', 'it', 'de', 'pt'];

export const LANG_LABELS: Record<Lang, string> = {
  fr: 'Français', en: 'English', es: 'Español', it: 'Italiano', de: 'Deutsch', pt: 'Português',
};

export const LANG_FLAGS: Record<Lang, string> = {
  fr: '🇫🇷', en: '🇬🇧', es: '🇪🇸', it: '🇮🇹', de: '🇩🇪', pt: '🇵🇹',
};

type TranslationEntry = Partial<Record<Lang, string>>;
type TranslationDict = Record<string, TranslationEntry>;

// ── Core translations (shared across all apps) ──

const CORE_TRANSLATIONS: TranslationDict = {
  // Navigation
  nav_accueil:        { fr:'Accueil', en:'Home', es:'Inicio', it:'Home', de:'Start', pt:'Início' },
  nav_compte:         { fr:'Mon compte', en:'My account', es:'Mi cuenta', it:'Il mio account', de:'Mein Konto', pt:'A minha conta' },

  // Statut
  status_open:        { fr:'Ouvert', en:'Open', es:'Abierto', it:'Aperto', de:'Geöffnet', pt:'Aberto' },
  status_closed:      { fr:'Fermé', en:'Closed', es:'Cerrado', it:'Chiuso', de:'Geschlossen', pt:'Fechado' },

  // CTA
  cta_appeler:        { fr:'Appeler', en:'Call', es:'Llamar', it:'Chiamare', de:'Anrufen', pt:'Ligar' },
  cta_discuter:       { fr:'Discuter', en:'Chat', es:'Chatear', it:'Chatta', de:'Chatten', pt:'Conversar' },
  cta_venir:          { fr:'Venir', en:'Directions', es:'Cómo llegar', it:'Indicazioni', de:'Anfahrt', pt:'Direções' },
  cta_partager:       { fr:'Partager', en:'Share', es:'Compartir', it:'Condividi', de:'Teilen', pt:'Partilhar' },

  // Jours
  day_lundi:          { fr:'Lundi', en:'Monday', es:'Lunes', it:'Lunedì', de:'Montag', pt:'Segunda' },
  day_mardi:          { fr:'Mardi', en:'Tuesday', es:'Martes', it:'Martedì', de:'Dienstag', pt:'Terça' },
  day_mercredi:       { fr:'Mercredi', en:'Wednesday', es:'Miércoles', it:'Mercoledì', de:'Mittwoch', pt:'Quarta' },
  day_jeudi:          { fr:'Jeudi', en:'Thursday', es:'Jueves', it:'Giovedì', de:'Donnerstag', pt:'Quinta' },
  day_vendredi:       { fr:'Vendredi', en:'Friday', es:'Viernes', it:'Venerdì', de:'Freitag', pt:'Sexta' },
  day_samedi:         { fr:'Samedi', en:'Saturday', es:'Sábado', it:'Sabato', de:'Samstag', pt:'Sábado' },
  day_dimanche:       { fr:'Dimanche', en:'Sunday', es:'Domingo', it:'Domenica', de:'Sonntag', pt:'Domingo' },
  horaires_aujourdhui:{ fr:'Aujourd\'hui', en:'Today', es:'Hoy', it:'Oggi', de:'Heute', pt:'Hoje' },
  horaires_demain:    { fr:'demain', en:'tomorrow', es:'mañana', it:'domani', de:'morgen', pt:'amanhã' },
  horaires_reouverture:{ fr:'Réouverture', en:'Reopens', es:'Reapertura', it:'Riapertura', de:'Wiedereröffnung', pt:'Reabertura' },

  // Événements
  events_titre:       { fr:'Nos prochains événements', en:'Upcoming events', es:'Próximos eventos', it:'Prossimi eventi', de:'Kommende Events', pt:'Próximos eventos' },
  events_voir_tout:   { fr:'Voir tout', en:'See all', es:'Ver todo', it:'Vedi tutto', de:'Alle anzeigen', pt:'Ver tudo' },
  events_places:      { fr:'places restantes', en:'spots left', es:'plazas disponibles', it:'posti disponibili', de:'Plätze frei', pt:'lugares disponíveis' },

  // Réservation
  slot_carte:          { fr:'Carte', en:'Menu', es:'Carta', it:'Menu', de:'Karte', pt:'Ementa' },
  slot_reserver:       { fr:'Réserver', en:'Book', es:'Reservar', it:'Prenota', de:'Buchen', pt:'Reservar' },

  // Cave
  cave_titre:         { fr:'Mon livre de cave', en:'My cellar book', es:'Mi libro de bodega', it:'Il mio libro di cantina', de:'Mein Kellerbuch', pt:'O meu livro de cave' },
  cave_bouteilles:    { fr:'bouteilles', en:'bottles', es:'botellas', it:'bottiglie', de:'Flaschen', pt:'garrafas' },
  cave_rechercher:    { fr:'Rechercher un vin...', en:'Search a wine...', es:'Buscar un vino...', it:'Cerca un vino...', de:'Wein suchen...', pt:'Pesquisar um vinho...' },
  cave_aucun:         { fr:'Aucune bouteille dans ta cave', en:'No bottles in your cellar', es:'Sin botellas en tu bodega', it:'Nessuna bottiglia in cantina', de:'Keine Flaschen im Keller', pt:'Sem garrafas na cave' },
  cave_ajouter:       { fr:'Ajouter un vin', en:'Add a wine', es:'Añadir un vino', it:'Aggiungi un vino', de:'Wein hinzufügen', pt:'Adicionar um vinho' },

  // Compte
  compte_titre:       { fr:'Mon compte', en:'My account', es:'Mi cuenta', it:'Il mio account', de:'Mein Konto', pt:'A minha conta' },
  compte_connecter:   { fr:'Se connecter', en:'Log in', es:'Iniciar sesión', it:'Accedi', de:'Einloggen', pt:'Iniciar sessão' },
  compte_deconnexion: { fr:'Déconnexion', en:'Log out', es:'Cerrar sesión', it:'Disconnetti', de:'Abmelden', pt:'Terminar sessão' },
  compte_fidelite:    { fr:'Programme de fidélité', en:'Loyalty program', es:'Programa de fidelidad', it:'Programma fedeltà', de:'Treueprogramm', pt:'Programa de fidelidade' },
  compte_recompenses: { fr:'Récompenses', en:'Rewards', es:'Recompensas', it:'Ricompense', de:'Belohnungen', pt:'Recompensas' },
  compte_parrainage:  { fr:'Parrainer un ami', en:'Refer a friend', es:'Recomendar a un amigo', it:'Invita un amico', de:'Freund empfehlen', pt:'Recomendar um amigo' },
  compte_carte_cadeau:{ fr:'Acheter une carte cadeau', en:'Buy a gift card', es:'Comprar una tarjeta regalo', it:'Acquista una carta regalo', de:'Geschenkkarte kaufen', pt:'Comprar um cartão presente' },

  // Profil
  profil_email:       { fr:'Email', en:'Email', es:'Email', it:'Email', de:'E-Mail', pt:'Email' },
  profil_telephone:   { fr:'Téléphone', en:'Phone', es:'Teléfono', it:'Telefono', de:'Telefon', pt:'Telefone' },
  profil_anniversaire:{ fr:'Anniversaire', en:'Birthday', es:'Cumpleaños', it:'Compleanno', de:'Geburtstag', pt:'Aniversário' },
  profil_adresse:     { fr:'Adresse', en:'Address', es:'Dirección', it:'Indirizzo', de:'Adresse', pt:'Morada' },

  // Boutons
  btn_annuler:        { fr:'Annuler', en:'Cancel', es:'Cancelar', it:'Annulla', de:'Abbrechen', pt:'Cancelar' },
  btn_fermer:         { fr:'Fermer', en:'Close', es:'Cerrar', it:'Chiudi', de:'Schließen', pt:'Fechar' },

  // Erreurs
  err_reseau:         { fr:'Erreur de connexion au serveur.', en:'Server connection error.', es:'Error de conexión al servidor.', it:'Errore di connessione al server.', de:'Server-Verbindungsfehler.', pt:'Erro de ligação ao servidor.' },

  // Langue
  langue_titre:       { fr:'Langue', en:'Language', es:'Idioma', it:'Lingua', de:'Sprache', pt:'Idioma' },

  // Avis
  avis_laisser:       { fr:'Laisser un avis', en:'Leave a review', es:'Dejar una reseña', it:'Lascia una recensione', de:'Bewertung abgeben', pt:'Deixar uma avaliação' },
  avis_count:         { fr:'avis', en:'reviews', es:'reseñas', it:'recensioni', de:'Bewertungen', pt:'avaliações' },
};

// ── Engine ──

interface I18nStorage {
  get(): Promise<string | null>;
  set(lang: string): Promise<void>;
}

let currentLang: Lang = 'fr';
let dict: TranslationDict = { ...CORE_TRANSLATIONS };
let listeners: Array<(lang: Lang) => void> = [];
let storage: I18nStorage | null = null;

/** Configure le backend de persistance (localStorage, AsyncStorage, etc.) */
export function setI18nStorage(s: I18nStorage): void {
  storage = s;
}

/** Ajoute des traductions (merge avec l'existant). Utile pour les modules. */
export function addTranslations(extra: TranslationDict): void {
  dict = { ...dict, ...extra };
}

/** Traduit une clé. */
export function t(key: string): string {
  const entry = dict[key];
  if (!entry) return key;
  return entry[currentLang] || entry.fr || key;
}

/** Change la langue courante. */
export function setLang(lang: Lang): void {
  if (!LANGS.includes(lang)) return;
  currentLang = lang;
  storage?.set(lang).catch(() => {});
  listeners.forEach((fn) => fn(lang));
}

export function getLang(): Lang {
  return currentLang;
}

/** S'abonne aux changements de langue. Retourne la fonction unsubscribe. */
export function onLangChange(fn: (lang: Lang) => void): () => void {
  listeners.push(fn);
  return () => { listeners = listeners.filter((f) => f !== fn); };
}

/** Charge la langue persistée (appeler au démarrage). */
export async function loadLang(): Promise<Lang> {
  if (storage) {
    try {
      const saved = await storage.get();
      if (saved && LANGS.includes(saved as Lang)) {
        currentLang = saved as Lang;
      }
    } catch {}
  }
  return currentLang;
}

export { CORE_TRANSLATIONS };
